import * as React from "react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { UnmountClosed } from "react-collapse";
import { ActionType } from "../../../../../react-core/Flux/ActionType";
import { ImageOptionDropdown } from "../../../../../react-core/Html/ImageOptionDropdown";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { AssetClassType } from "../../../../../Wrap/Constants/Enums/Domain/Instrument/AssetClassType";
import { ProductSort } from "../../../../../Wrap/Constants/Enums/Domain/ProductSort";
import { ProductType } from "../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { SubAccountType } from "../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ServiceProposition } from "../../../../../Wrap/Constants/Enums/ServiceProposition";
import { SortDirection } from "../../../../../Wrap/Core/SortingAndPaging/SortDirection";
import { ExchangeTradedInstrumentSearchQuery } from "../../../../../Wrap/Models/Domains/Investment/ExchangeTradedInstrumentSearchQuery";
import { ResearchISTDispatcher } from "../../../../Dashboard/Components/ToolsAndLiterature/Store/Variable";
import { InvestmentSelectionAction } from "../../Action/InvestmentSelectionAction";
import { AllocationTableItem } from "../../Store/SelectedInvesmentStoreStatic";
import { ExchangeTradedResults } from "./ExchangeTradedResults";

interface IExchangeTradedSearchProps {
    hiercharyId: string;
    type: SubAccountType;
    tableData: AllocationTableItem[];
    index?: number;
    researchIst: boolean;
    accountServiceProposition: ServiceProposition;
    canBeBought: boolean;
    canBeSold: boolean;
    canBeTransferred?: boolean;
}

export const ExcangeTradedSearch = (props: IExchangeTradedSearchProps, ref: any) => {
    const [showResults, setShowResults] = useState(false);
    const [sdrLabelsOptions, setSdrLabelOptions] = useState([]);
    const [sdrLabel, setSdrLabel] = useState(null);
    const [assetTier1Opitons, setAssetTier1Options] = useState([]);
    const [assetTier2Opitons, setAssetTier2Options] = useState([]);
    const [sectorOptions, setSectorOptions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [sector, setSector] = useState(null);
    const [tier2, setTier2] = useState(null);
    const [tier1, setTier1] = useState(null);
    const [status, setStatus] = useState(null);
    const [data, setData] = useState(null);
    const [canBeTransferred, setCanBeTransferred] = useState(props.canBeTransferred);

    useImperativeHandle(ref, () => ({
        close: () => {
            setShowResults(false);
        },
    }));

    const getAssetClass = (type: AssetClassType) => {
        InvestmentSelectionAction.getAssetClasses(props.hiercharyId, type, (data) => {
            type == AssetClassType.Tier1 ? setAssetTier1Options(data) : setAssetTier2Options(data);
        });
    };

    const getSectorOptions = () => {
        InvestmentSelectionAction.getSectors((data) => setSectorOptions(data));
    };

    const getSdrLabelOptions = () => {
        InvestmentSelectionAction.getSdrUrls((data) => {
            const sustainTypeOptions = data.map((x) => ({
                imgUrl: x.SDRUrl,
                value: x.SDRLabel,
            }));
            sustainTypeOptions.push({ imgUrl: "All label", value: "Sustainability Label" });
            setSdrLabelOptions(sustainTypeOptions);
        });
    };

    useEffect(() => {
        getSectorOptions();
        getSdrLabelOptions();
        getAssetClass(AssetClassType.Tier2);
        getAssetClass(AssetClassType.Tier1);
    }, []);

    const searchExchangeTraded = (index) => {
        const query: ExchangeTradedInstrumentSearchQuery = {
            CurrentPage: index,
            Sector: sector ? sector : null,
            SdrLabel: sdrLabel === "All" ? null : sdrLabel === "Sustainability Label" ? "All" : sdrLabel || null,
        };
        if (props.canBeBought) {
            query["CanBeBought"] = true;
        }
        if (props.canBeSold) {
            query["CanBeSold"] = true;
        }
        query["CanBeTransferred"] = canBeTransferred;
        query["AssetClass"] = tier1;
        query["AssetClassT2"] = tier2;
        query["ProductWrapperTypes"] = [props.type];
        query["Sorting"] = [{ Field: ProductSort.Name, Direction: SortDirection.Ascending }];
        query["ServiceProposition"] = props.accountServiceProposition;

        if (props.researchIst) {
            ResearchISTDispatcher.dispatch({
                actionType: ActionType.Update,
                payload: (curPage: number) => {
                    const _query = JSON.parse(JSON.stringify(query));
                    _query.CurrentPage = curPage;
                    getData(_query);
                },
            });
        }
        setIsSearching(true);
        getData(query);
    };
    const getData = (query) => {
        setIsSearching(false);
        InvestmentSelectionAction.searchExchangedTrade(query, props.hiercharyId, (data) => {
            if (props.researchIst) {
                ResearchISTDispatcher.dispatch({ actionType: ActionType.Success, payload: data });
            } else {
                setData(data);
                setShowResults(true);
            }
        });
    };

    const onStatusChange = (newStatus: string) => {
        setStatus(newStatus);
        setCanBeTransferred(newStatus !== "Tradable");
    };

    return (
        <>
            <div className="ist-advanced-panel">
                <div className="row">
                    <div className="col-6">
                        <SelectEditor
                            label="Asset class T1"
                            placeholder="Please select"
                            options={assetTier1Opitons}
                            getText={(o) => o.Label}
                            onChange={(v) => {
                                setTier1(v?.Label);
                            }}
                        />
                    </div>
                    <div className="col-6">
                        <SelectEditor
                            label="Asset class T2"
                            placeholder="Please select"
                            options={assetTier2Opitons}
                            getText={(o) => o.Label}
                            onChange={(v) => {
                                setTier2(v?.Label);
                            }}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-4">
                        <SelectEditor
                            label="Status"
                            placeholder="Please select"
                            options={["Buy tradable", "Tradable"]}
                            getText={(o) => o}
                            onChange={onStatusChange}
                        />
                    </div>

                    <div className="col-12 col-sm-6 col-md-4">
                        <div className="form-group">
                            <label className="control-label">Sustainability type</label>
                            <ImageOptionDropdown
                                cssWrapper="is-fluid"
                                label="Please select"
                                options={sdrLabelsOptions}
                                onChange={(v) => {
                                    setSdrLabel(v);
                                }}
                            />
                        </div>
                    </div>

                    <div className="col-4 text-right">
                        <button
                            onClick={() => searchExchangeTraded(1)}
                            type="button"
                            className={`btn secondary label-offset ${isSearching ? "is-busy" : ""}`}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            <UnmountClosed isOpened={showResults}>
                {!props.researchIst && data && (
                    <ExchangeTradedResults
                        data={data}
                        allocationTable={props.tableData}
                        onPageChange={(i) => searchExchangeTraded(i)}
                        index={props.index}
                        researchIst={props.researchIst}
                    />
                )}
            </UnmountClosed>
        </>
    );
};

export default forwardRef(ExcangeTradedSearch);
